import React from 'react'
import { Link } from 'react-router-dom'
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
    Button,
} from '@material-ui/core'
import {
    ArrowBack as BackIcon,
    GetApp as DownloadIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'

import {
    LeaseAuditDetail,
    STEP_LABELS,
    TERM_STATUS_FILTER_LABELS,
} from '../types'
import { useDetailHeaderStyles } from './styles'

export interface DetailHeaderProps {
    leaseAudit: LeaseAuditDetail | undefined
    isLoading: boolean
    isGeneratingReport: boolean
    onGenerateReport: () => void
    showStepIndicator: boolean
    onToggleStepIndicator: () => void
    showTermSelection: boolean
    onToggleTermSelection: () => void
    backPath?: string
}

export const DetailHeader: React.FC<DetailHeaderProps> = ({
    leaseAudit,
    isLoading,
    isGeneratingReport,
    onGenerateReport,
    showStepIndicator,
    onToggleStepIndicator,
    showTermSelection,
    onToggleTermSelection,
    backPath = '/lease-audit',
}) => {
    const classes = useDetailHeaderStyles()

    if (isLoading || !leaseAudit) {
        return (
            <Box className={classes.header}>
                <Box className={classes.leftSection}>
                    <Link to={backPath}>
                        <IconButton size="small" className={classes.backButton}>
                            <BackIcon />
                        </IconButton>
                    </Link>
                    <CircularProgress size={24} />
                </Box>
            </Box>
        )
    }

    const isReportEnabled = leaseAudit.current_step === 'AUDITING'

    return (
        <Box className={classes.header}>
            <Box className={classes.leftSection}>
                <Link to={backPath}>
                    <IconButton size="small" className={classes.backButton}>
                        <BackIcon />
                    </IconButton>
                </Link>

                <Typography className={classes.title}>
                    {leaseAudit.name}
                </Typography>
            </Box>

            <Box className={classes.rightSection}>
                <Button
                    size="small"
                    onClick={onToggleStepIndicator}
                    startIcon={
                        showStepIndicator ? (
                            <ExpandLessIcon />
                        ) : (
                            <ExpandMoreIcon />
                        )
                    }
                    className={classes.stepsButton}
                >
                    <Box className={classes.buttonLabel}>
                        <span>
                            {showStepIndicator ? 'Hide Steps' : 'Show Steps'}
                        </span>
                        <span className={classes.buttonValue}>
                            {STEP_LABELS[leaseAudit.current_step]}
                        </span>
                    </Box>
                </Button>
                <Button
                    size="small"
                    onClick={onToggleTermSelection}
                    startIcon={
                        showTermSelection ? (
                            <ExpandLessIcon />
                        ) : (
                            <ExpandMoreIcon />
                        )
                    }
                    className={classes.stepsButton}
                >
                    <Box className={classes.buttonLabel}>
                        <span>
                            {showTermSelection
                                ? 'Hide Term Select'
                                : 'Select Terms'}
                        </span>
                        <span className={classes.buttonValue}>
                            {
                                TERM_STATUS_FILTER_LABELS[
                                    leaseAudit.term_status_filter
                                ]
                            }
                        </span>
                    </Box>
                </Button>

                <Tooltip
                    title={
                        isReportEnabled
                            ? 'Download Report'
                            : 'Report available after auditing'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={onGenerateReport}
                            disabled={isGeneratingReport || !isReportEnabled}
                            className={`${classes.actionButton} ${classes.downloadButton}`}
                        >
                            {isGeneratingReport ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <DownloadIcon />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    )
}
